import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Export the api instance for direct use
export { api };

// Auth API
export const authAPI = {
  register: (data: { email: string; password: string; firstName: string; lastName: string; companyName?: string; industrySlug?: string }) =>
    api.post('/auth/register', data),
  
  login: (data: { email: string; password: string }) =>
    api.post('/auth/login', data),
  
  logout: () => api.post('/auth/logout'),
  
  getMe: () => api.get('/auth/me'),
  
  forgotPassword: (email: string) =>
    api.post('/auth/forgot-password', { email }),
  
  resetPassword: (token: string, password: string) =>
    api.post('/auth/reset-password', { token, password }),
};

// User API
export const userAPI = {
  getProfile: () => api.get('/users/profile'),

  updateProfile: (data: any) => api.put('/users/profile', data),

  updateEmailSignature: (emailSignature: string) =>
    api.put('/users/email-signature', { emailSignature }),

  changePassword: (currentPassword: string, newPassword: string) =>
    api.put('/users/change-password', { currentPassword, newPassword }),

  completeOnboarding: () => api.put('/users/complete-onboarding'),

  fetchWebsiteBrand: (url: string) => api.get('/users/website-brand', { params: { url } }),

  discoverListings: (industry: string) => api.post('/users/discover-listings', { industry }),

  deleteAccount: () => api.delete('/users/account'),

  getForwardingAddress: () => api.get('/users/forwarding-address'),

  getPublicProfile: (slug: string) => api.get(`/users/public-profile/${slug}`),

  updateProfilePageSettings: (data: { profilePageEnabled?: boolean; profileSlug?: string }) =>
    api.put('/users/profile-page-settings', data),
};

// Contacts API
export const contactsAPI = {
  getAll: (params?: { search?: string; tags?: string[]; sortBy?: string; sortOrder?: string; limit?: number; offset?: number; referredById?: string }) =>
    api.get('/contacts', { params }),
  
  getById: (id: string) => api.get(`/contacts/${id}`),
  
  create: (data: any) => api.post('/contacts', data),
  
  update: (id: string, data: any) => api.put(`/contacts/${id}`, data),
  
  delete: (id: string) => api.delete(`/contacts/${id}`),
  
  bulkDelete: (ids: string[]) => api.post('/contacts/bulk-delete', { ids }),
  
  getTags: () => api.get('/contacts/tags/all'),

  scanBusinessCard: (imageFile: File) => {
    const formData = new FormData();
    formData.append('image', imageFile);
    return api.post('/contacts/scan-card', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  
  // Referral tracking
  searchReferrers: (q: string) => api.get('/contacts/search-referrers', { params: { q } }),

  getReferrals: (contactId: string) => api.get(`/contacts/${contactId}/referrals`),

  // Duplicate and merge operations
  searchDuplicates: (search: string) => api.get(`/contacts/duplicates?search=${encodeURIComponent(search)}`),
  
  merge: (data: { primaryId: string; mergeIds: string[] }) => api.post('/contacts/merge', data),
  
  mergeWithFieldSelection: (data: {
    primaryId: string;
    mergeIds: string[];
    mergedData: any
  }) => api.post('/contacts/merge-with-fields', data),

  getSharedEmailContacts: (contactId: string) => api.get(`/contacts/${contactId}/shared-email-contacts`),
};

// Custom Fields API
export const customFieldsAPI = {
  getAll: () => api.get('/custom-fields'),
  
  getById: (id: string) => api.get(`/custom-fields/${id}`),
  
  create: (data: any) => api.post('/custom-fields', data),
  
  update: (id: string, data: any) => api.put(`/custom-fields/${id}`, data),
  
  delete: (id: string) => api.delete(`/custom-fields/${id}`),
  
  reorder: (fields: { id: string }[]) => api.put('/custom-fields/reorder', { fields }),
};

// Stages API
export const stagesAPI = {
  getAll: (params?: { pipelineType?: 'sales' | 'recruiting' }) => 
    api.get('/stages', { params }),
  
  create: (data: { name: string; color?: string; order?: number; pipelineType?: 'sales' | 'recruiting' }) => 
    api.post('/stages', data),
  
  update: (id: string, data: any) => api.put(`/stages/${id}`, data),
  
  delete: (id: string) => api.delete(`/stages/${id}`),
  
  reorder: (stages: { id: string; order: number }[]) => api.put('/stages/reorder', { stages }),
  
  initialize: (pipelineType: 'sales' | 'recruiting' = 'sales') => 
    api.post('/stages/initialize', { pipelineType }),
};

// Deals API
export const dealsAPI = {
  getAll: (params?: { 
    search?: string; 
    stageId?: string; 
    status?: string; 
    contactId?: string;
    sortBy?: string; 
    sortOrder?: string; 
    limit?: number; 
    offset?: number 
  }) => api.get('/deals', { params }),
  
  getById: (id: string) => api.get(`/deals/${id}`),
  
  create: (data: {
    name: string;
    value?: number;
    stageId: string;
    contactId?: string;
    notes?: string;
    expectedCloseDate?: string;
  }) => api.post('/deals', data),
  
  update: (id: string, data: any) => api.put(`/deals/${id}`, data),
  
  updateStage: (id: string, stageId: string, position?: number) =>
    api.patch(`/deals/${id}/stage`, { stageId, position }),

  reorder: (stageId: string, dealIds: string[]) =>
    api.put('/deals/reorder', { stageId, dealIds }),

  updateRating: (id: string, rating: number | null) => api.patch(`/deals/${id}/rating`, { rating }),

  delete: (id: string) => api.delete(`/deals/${id}`),

  // Deal contacts (team members)
  addContact: (dealId: string, data: { contactId: string; role: string; notes?: string }) =>
    api.post(`/deals/${dealId}/contacts`, data),
  updateContact: (dealId: string, dealContactId: string, data: { role?: string; notes?: string }) =>
    api.put(`/deals/${dealId}/contacts/${dealContactId}`, data),
  removeContact: (dealId: string, dealContactId: string) =>
    api.delete(`/deals/${dealId}/contacts/${dealContactId}`),

  getStageHistory: (dealId: string) => api.get(`/deals/${dealId}/stage-history`),

  getStageVelocity: (params?: { dateFrom?: string; dateTo?: string; ownerId?: string }) =>
    api.get('/deals/stage-velocity', { params }),
};

// Deal Custom Fields API
export const dealCustomFieldsAPI = {
  getAll: () => api.get('/deal-custom-fields'),
  create: (data: any) => api.post('/deal-custom-fields', data),
  update: (id: string, data: any) => api.put(`/deal-custom-fields/${id}`, data),
  delete: (id: string) => api.delete(`/deal-custom-fields/${id}`),
  reorder: (fieldIds: string[]) => api.post('/deal-custom-fields/reorder', { fieldIds })
};

// Automations API
export const automationsAPI = {
  getAll: (scope?: 'personal' | 'company') => api.get('/automations', { params: scope ? { scope } : {} }),

  getOrgUsers: () => api.get('/automations/meta/org-users'),
  
  getById: (id: string) => api.get(`/automations/${id}`),
  
  create: (data: {
    name: string;
    description?: string;
    trigger: any;
    conditions?: any[];
    actions: any[];
  }) => api.post('/automations', data),
  
  update: (id: string, data: any) => api.put(`/automations/${id}`, data),
  
  toggle: (id: string) => api.patch(`/automations/${id}/toggle`),
  
  delete: (id: string) => api.delete(`/automations/${id}`),
  
  getLogs: (id: string, params?: { limit?: number; offset?: number }) => 
    api.get(`/automations/${id}/logs`, { params }),
  
  test: (id: string, testData?: any) => api.post(`/automations/${id}/test`, { testData }),
  
  getEnrollments: (id: string) => api.get(`/automations/${id}/enrollments`),
  
  previewEnrollment: (id: string) => api.get(`/automations/${id}/preview-enrollment`),
  
  enroll: (id: string, entityType: 'contact' | 'deal', entityIds: string[]) => 
    api.post(`/automations/${id}/enroll`, { entityType, entityIds }),
  
  unenroll: (id: string, entityType: 'contact' | 'deal', entityId: string) => 
    api.post(`/automations/${id}/unenroll`, { entityType, entityId }),
  
  getFields: (entityType: 'contact' | 'deal') => 
    api.get(`/automations/fields/${entityType}`),
  
  getEntityDebugInfo: (entityType: 'contact' | 'deal', entityId: string) =>
    api.get(`/automations/debug/entity/${entityType}/${entityId}`),
  
  processEnrollment: (enrollmentId: string) =>
    api.post(`/automations/enrollment/${enrollmentId}/process`),

  // Multi-step automation endpoints
  createMultiStep: (data: any) => api.post('/automations/multi-step', data),
  
  updateMultiStep: (id: string, data: any) => api.put(`/automations/${id}/multi-step`, data),
  
  getWithSteps: (id: string) => api.get(`/automations/${id}/with-steps`),
  
  validateWorkflow: (id: string) => api.post(`/automations/${id}/validate`),
  
  // Exit criteria endpoints
  getExitCriteria: (id: string) => api.get(`/automations/${id}/exit-criteria`),
  
  updateExitCriteria: (id: string, data: {
    exitCriteria: any;
    maxDurationDays?: number;
    safetyExitEnabled?: boolean;
  }) => api.post(`/automations/${id}/exit-criteria`, data),
  
  testExitConditions: (id: string, entityType: 'contact' | 'deal', entityId: string) =>
    api.post(`/automations/${id}/test-exit`, { entityType, entityId }),
  
  getExitStats: (id: string) => api.get(`/automations/${id}/exit-stats`),
};

// Email Analytics API
export const analyticsAPI = {
  getOverview: (params?: { startDate?: string; endDate?: string }) =>
    api.get('/analytics/overview', { params }),

  getContactAnalytics: (contactId: string) =>
    api.get(`/analytics/contact/${contactId}`),

  getCampaignPerformance: (period: '24h' | '7d' | '30d' | '90d' = '7d') =>
    api.get('/analytics/campaign-performance', { params: { period } }),

  getLinkAnalytics: (limit: number = 20) =>
    api.get('/analytics/links', { params: { limit } }),

  getUnsubscribeAnalytics: (params?: { startDate?: string; endDate?: string; limit?: number }) =>
    api.get('/analytics/unsubscribes', { params }),

  getDashboard: () =>
    api.get('/analytics/dashboard'),

  // Open/click totals for a date range (dashboard engagement tile)
  getEmailEngagement: (days: number) =>
    api.get('/analytics/email-engagement', { params: { days } }),

  // Who opened / who clicked inside that same range
  getEmailEngagementContacts: (params: { days: number; eventType: 'open' | 'click'; limit?: number }) =>
    api.get('/analytics/email-engagement/contacts', { params }),

  // Contacts who opened OR clicked inside the active engagement window.
  // The window length is set server-side and returned as `windowDays`.
  getActiveContacts: (params?: { limit?: number }) =>
    api.get('/analytics/active-contacts', { params }),

  triggerAlerts: () =>
    api.post('/analytics/trigger-alerts'),

  getActivityReports: (params?: { startDate?: string; endDate?: string; activityType?: string }) =>
    api.get('/analytics/activities', { params }),

  updateWeeklyGoal: (goal: number) =>
    api.put('/analytics/weekly-goal', { goal }),
};

// Notes API
export const notesAPI = {
  getContactNotes: (contactId: string) =>
    api.get(`/notes/contact/${contactId}`),

  getDealNotes: (dealId: string) =>
    api.get(`/notes/deal/${dealId}`),

  getNoteAudio: (noteId: string) =>
    api.get(`/notes/${noteId}/audio`),

  createNote: (data: {
    contactId?: string;
    dealId?: string;
    content: string;
    activities?: string[];
    audioData?: string;
    audioDuration?: number;
    audioMimeType?: string;
    notifyContactIds?: string[];
  }) => api.post('/notes', data),

  updateNote: (id: string, data: { content?: string; isPinned?: boolean; activities?: string[] }) =>
    api.put(`/notes/${id}`, data),

  deleteNote: (id: string) =>
    api.delete(`/notes/${id}`),
};

// Positions API (Recruiting)
export const positionsAPI = {
  getAll: (params?: { 
    status?: string; 
    search?: string; 
    limit?: number; 
    offset?: number 
  }) => api.get('/positions', { params }),
  
  getById: (id: string) => api.get(`/positions/${id}`),
  
  create: (data: any) => api.post('/positions', data),
  
  update: (id: string, data: any) => api.put(`/positions/${id}`, data),
  
  delete: (id: string) => api.delete(`/positions/${id}`),
  
  bulkUpdate: (positionIds: string[], updates: any) => 
    api.put('/positions/bulk/update', { positionIds, updates }),
};

// Recruiting Pipeline API
export const recruitingAPI = {
  getAll: (params?: { 
    positionId?: string;
    status?: string;
    stageId?: string;
    search?: string;
    limit?: number; 
    offset?: number 
  }) => api.get('/recruiting-pipeline', { params }),
  
  getById: (id: string) => api.get(`/recruiting-pipeline/${id}`),
  
  addCandidate: (data: {
    candidateId: string;
    positionId: string;
    stageId?: string;
    status?: string;
    rating?: number;
    notes?: string;
    interviewDate?: Date;
    customFields?: any;
  }) => api.post('/recruiting-pipeline', data),
  
  update: (id: string, data: any) => api.put(`/recruiting-pipeline/${id}`, data),
  
  moveCandidate: (id: string, stageId: string) => 
    api.put(`/recruiting-pipeline/${id}/move`, { stageId }),
  
  remove: (id: string) => api.delete(`/recruiting-pipeline/${id}`),
  
  bulkMove: (candidateIds: string[], stageId: string) =>
    api.put('/recruiting-pipeline/bulk/move', { candidateIds, stageId }),
};

// Reminders API
export const remindersAPI = {
  getAll: (params?: {
    completed?: boolean;
    entityType?: 'contact' | 'deal';
    limit?: number;
    offset?: number;
  }) => api.get('/reminders', { params }),

  create: (data: {
    title: string;
    description?: string;
    remindAt: string;
    entityType?: 'contact' | 'deal';
    entityId?: string;
    entityName?: string;
  }) => api.post('/reminders', data),

  getById: (id: string) => api.get(`/reminders/${id}`),

  update: (id: string, data?: { isCompleted?: boolean }) =>
    api.put(`/reminders/${id}`, data),

  delete: (id: string) => api.delete(`/reminders/${id}`),

  checkDue: () => api.get('/reminders/check/due'),

  getDashboardSummary: () => api.get('/reminders/dashboard/summary'),

  bulkAction: (action: 'complete' | 'incomplete' | 'delete', reminderIds: string[]) =>
    api.post('/reminders/bulk', { action, reminderIds })
};

// Push Notification API
export const pushAPI = {
  // Get VAPID public key for push subscription
  getVapidPublicKey: () => api.get('/push/vapid-public-key'),

  // Subscribe to push notifications
  subscribe: (data: {
    subscription: PushSubscriptionJSON;
    deviceInfo?: {
      userAgent: string;
      platform: string;
      isIOS: boolean;
      isPWA: boolean;
    };
  }) => api.post('/push/subscribe', data),

  // Unsubscribe from push notifications
  unsubscribe: (data: { endpoint: string }) =>
    api.post('/push/unsubscribe', data),

  // Send test notification
  test: () => api.post('/push/test'),

  // Get user's push subscriptions
  getSubscriptions: () => api.get('/push/subscriptions'),
};

// Companies API
export const companiesAPI = {
  getAll: (params?: {
    search?: string;
    state?: string;
    city?: string;
    sortBy?: string;
    sortOrder?: string;
    limit?: number;
    offset?: number
  }) => api.get('/companies', { params }),

  getById: (id: string) => api.get(`/companies/${id}`),

  create: (data: any) => api.post('/companies', data),

  update: (id: string, data: any) => api.put(`/companies/${id}`, data),

  delete: (id: string) => api.delete(`/companies/${id}`),

  bulkDelete: (ids: string[]) => api.post('/companies/bulk-delete', { ids }),

  getGroupings: () => api.get<{
    byState: { state: string; count: number }[];
    byCityState: { city: string; state: string; count: number }[];
  }>('/companies/groupings'),
};

// Company Custom Fields API (admin-only for create/update/delete)
export const companyCustomFieldsAPI = {
  getAll: () => api.get('/company-custom-fields'),
  create: (data: any) => api.post('/company-custom-fields', data),
  update: (id: string, data: any) => api.put(`/company-custom-fields/${id}`, data),
  delete: (id: string) => api.delete(`/company-custom-fields/${id}`),
  reorder: (fieldIds: string[]) => api.post('/company-custom-fields/reorder', { fieldIds })
};

// Industries API (Super Admin)
export const industriesAPI = {
  getAll: (params?: {
    search?: string;
    status?: 'active' | 'inactive';
    sortBy?: string;
    sortOrder?: string;
  }) => api.get('/super-admin/industries', { params }),

  getById: (id: string) => api.get(`/super-admin/industries/${id}`),

  create: (data: {
    name: string;
    slug?: string;
    description?: string;
    isActive?: boolean;
    dealFieldTemplates?: any[];
    candidateFieldTemplates?: any[];
  }) => api.post('/super-admin/industries', data),

  update: (id: string, data: {
    name?: string;
    slug?: string;
    description?: string;
    isActive?: boolean;
  }) => api.put(`/super-admin/industries/${id}`, data),

  // Deal field templates
  getDealFields: (id: string) => api.get(`/super-admin/industries/${id}/deal-fields`),

  updateDealFields: (id: string, fields: any[]) =>
    api.put(`/super-admin/industries/${id}/deal-fields`, { fields }),

  // Candidate field templates
  getCandidateFields: (id: string) => api.get(`/super-admin/industries/${id}/candidate-fields`),

  updateCandidateFields: (id: string, fields: any[]) =>
    api.put(`/super-admin/industries/${id}/candidate-fields`, { fields }),

  // UI configuration
  getUiConfig: (id: string) => api.get(`/super-admin/industries/${id}/ui-config`),

  updateUiConfig: (id: string, uiConfig: any) =>
    api.put(`/super-admin/industries/${id}/ui-config`, { uiConfig }),

  // Pipeline stages
  getPipelineStages: (id: string) => api.get(`/super-admin/industries/${id}/pipeline-stages`),

  updatePipelineStages: (id: string, pipelineStages: any) =>
    api.put(`/super-admin/industries/${id}/pipeline-stages`, { pipelineStages }),

  // Feature flags
  getFeatureFlags: (id: string) => api.get(`/super-admin/industries/${id}/feature-flags`),

  updateFeatureFlags: (id: string, featureFlags: any) =>
    api.put(`/super-admin/industries/${id}/feature-flags`, { featureFlags }),

  // Email templates
  getEmailTemplates: (id: string) => api.get(`/super-admin/industries/${id}/email-templates`),

  updateEmailTemplates: (id: string, templates: any[]) =>
    api.put(`/super-admin/industries/${id}/email-templates`, { templates }),
};

// Onboarding API
export const onboardingAPI = {
  // Templates
  getTemplates: () => api.get('/onboarding/templates'),
  createTemplate: (data: { title: string; description?: string; fields?: any[]; order?: number }) =>
    api.post('/onboarding/templates', data),
  updateTemplate: (id: string, data: any) => api.put(`/onboarding/templates/${id}`, data),
  deleteTemplate: (id: string) => api.delete(`/onboarding/templates/${id}`),
  reorderTemplates: (templateIds: string[]) =>
    api.put('/onboarding/templates/reorder', { templateIds }),

  // Client Onboardings
  getAll: (params?: { status?: string; contactId?: string; companyId?: string }) =>
    api.get('/onboarding', { params }),
  getById: (id: string) => api.get(`/onboarding/${id}`),
  create: (data: { contactId?: string; companyId?: string; assignedTo?: string; notes?: string }) =>
    api.post('/onboarding', data),
  update: (id: string, data: { status?: string; assignedTo?: string; notes?: string }) =>
    api.put(`/onboarding/${id}`, data),
  delete: (id: string) => api.delete(`/onboarding/${id}`),

  // Tasks
  updateTask: (onboardingId: string, taskId: string, data: { status?: string; fieldValues?: any; notes?: string }) =>
    api.put(`/onboarding/${onboardingId}/tasks/${taskId}`, data),
};

// Deduplication API
export const deduplicationAPI = {
  getRules: () => api.get('/deduplication-rules'),
  createRule: (data: { name: string; matchFields: string[] }) =>
    api.post('/deduplication-rules', data),
  updateRule: (id: string, data: { name?: string; matchFields?: string[]; isEnabled?: boolean; priority?: number }) =>
    api.put(`/deduplication-rules/${id}`, data),
  deleteRule: (id: string) => api.delete(`/deduplication-rules/${id}`),
  scanNow: () => api.post('/deduplication-rules/scan'),
  getDuplicates: (params?: { status?: string; page?: number; limit?: number }) =>
    api.get('/deduplication-rules/duplicates', { params }),
  getDuplicateCount: () => api.get('/deduplication-rules/duplicates/count'),
  dismissDuplicate: (id: string) => api.put(`/deduplication-rules/duplicates/${id}/dismiss`),
  resolveDuplicate: (id: string) => api.put(`/deduplication-rules/duplicates/${id}/resolve`),
};

// Field History API
export const fieldHistoryAPI = {
  getContactHistory: (contactId: string, params?: { field?: string; source?: string; limit?: number; offset?: number }) =>
    api.get(`/field-history/contacts/${contactId}`, { params }),
};

// Tags API
export const tagsAPI = {
  getAll: (params?: { search?: string; type?: string }) =>
    api.get('/tags', { params }),
  create: (data: { name: string; type: 'admin' | 'user'; color?: string }) =>
    api.post('/tags', data),
  update: (id: string, data: { name?: string; color?: string }) =>
    api.put(`/tags/${id}`, data),
  delete: (id: string) => api.delete(`/tags/${id}`),
  promote: (id: string) => api.put(`/tags/${id}/promote`),
};

// Contact Groups API
export const contactGroupsAPI = {
  getAll: (params?: { scope?: string; type?: string; search?: string }) =>
    api.get('/contact-groups', { params }),
  getById: (id: string) => api.get(`/contact-groups/${id}`),
  create: (data: { name: string; type: 'static' | 'dynamic'; scope?: string; description?: string; criteria?: any }) =>
    api.post('/contact-groups', data),
  update: (id: string, data: { name?: string; description?: string; scope?: string; criteria?: any }) =>
    api.put(`/contact-groups/${id}`, data),
  delete: (id: string) => api.delete(`/contact-groups/${id}`),
  duplicate: (id: string) => api.post(`/contact-groups/${id}/duplicate`),
  getMembers: (id: string, params?: { limit?: number; offset?: number; search?: string }) =>
    api.get(`/contact-groups/${id}/members`, { params }),
  addMembers: (id: string, contactIds: string[]) =>
    api.post(`/contact-groups/${id}/members`, { contactIds }),
  removeMembers: (id: string, contactIds: string[]) =>
    api.delete(`/contact-groups/${id}/members`, { data: { contactIds } }),
  getContactGroups: (contactId: string) =>
    api.get(`/contact-groups/contacts/${contactId}/groups`),
  getOverlap: (groupIds: string[]) =>
    api.get('/contact-groups/overlap', { params: { groupIds: groupIds.join(',') } }),
};

// Forms API
export const formsAPI = {
  list: (params?: { scope?: string; status?: string }) =>
    api.get('/forms', { params }),
  get: (id: string) => api.get(`/forms/${id}`),
  create: (data: Record<string, any>) => api.post('/forms', data),
  update: (id: string, data: Record<string, any>) => api.put(`/forms/${id}`, data),
  delete: (id: string) => api.delete(`/forms/${id}`),
  duplicate: (id: string) => api.post(`/forms/${id}/duplicate`),
  publish: (id: string) => api.put(`/forms/${id}/publish`),
  unpublish: (id: string) => api.put(`/forms/${id}/unpublish`),
  cloneDefault: (defaultIndex: number) => api.post('/forms/clone-default', { defaultIndex }),
  getSubmissions: (id: string, params?: { page?: number; limit?: number; status?: string }) =>
    api.get(`/forms/${id}/submissions`, { params }),
  getPublic: (slug: string) => api.get(`/forms/public/${slug}`),
  submitPublic: (slug: string, data: Record<string, any>) =>
    api.post(`/forms/public/${slug}/submit`, { data }),
  getAnalytics: (params?: { formId?: string; period?: string }) =>
    api.get('/forms/analytics/stats', { params }),
};

// Refinance Opportunities API
export const refinanceAPI = {
  getOpportunities: (params?: {
    status?: string;
    opportunityType?: string;
    sortBy?: string;
    sortOrder?: string;
    limit?: number;
    offset?: number;
  }) => api.get('/refinance-opportunities', { params }),

  getSummary: () => api.get('/refinance-opportunities/summary'),

  updateStatus: (id: string, data: { status: string; convertedDealId?: string }) =>
    api.patch(`/refinance-opportunities/${id}/status`, data),

  scan: () => api.post('/refinance-opportunities/scan'),

  getMarketRates: () => api.get('/market-rates'),

  updateMarketRates: (rates: { productType: string; rate: number }[]) =>
    api.put('/market-rates', { rates }),
};

export const playbookAPI = {
  getConfig: () => api.get('/playbook/config'),

  updateConfig: (dayOfWeek: number, data: {
    label: string;
    contactGroupId?: string | null;
    coachingTip?: string | null;
    callWindowStart?: string | null;
    callWindowEnd?: string | null;
    weeklyTarget?: number | null;
    sortField?: string | null;
    sortDirection?: string;
    displayColumns?: string[] | null;
    alphabetRotation?: boolean;
    isActive?: boolean;
  }) => api.put(`/playbook/config/${dayOfWeek}`, data),

  deleteConfig: (dayOfWeek: number) => api.delete(`/playbook/config/${dayOfWeek}`),

  getToday: () => api.get('/playbook/today'),

  getDay: (dayOfWeek: number) => api.get(`/playbook/day/${dayOfWeek}`),

  logCall: (data: { playbookConfigId: string; contactId: string; notes?: string }) =>
    api.post('/playbook/log-call', data),

  deleteCallLog: (id: string) => api.delete(`/playbook/log-call/${id}`),
};

export const videoAPI = {
  resolve: (url: string) => api.get('/video/resolve', { params: { url } }),
};

export const emailDraftsAPI = {
  list: (params?: { status?: string; contactId?: string }) =>
    api.get('/email-drafts', { params }),

  get: (id: string) => api.get(`/email-drafts/${id}`),

  update: (id: string, data: { subject?: string; body?: string; personal?: boolean }) =>
    api.put(`/email-drafts/${id}`, data),

  schedule: (id: string, scheduledAt: string) =>
    api.post(`/email-drafts/${id}/schedule`, { scheduledAt }),

  scheduleBulk: (ids: string[], scheduledAt: string) =>
    api.post('/email-drafts/schedule-bulk', { ids, scheduledAt }),

  unschedule: (id: string) => api.post(`/email-drafts/${id}/unschedule`),

  cancel: (id: string) => api.post(`/email-drafts/${id}/cancel`),
};

export default api;